import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { projects } from '../mock';

const Projects = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  // Translate the card strip horizontally as the user scrolls down
  // 6 cards × (600 + 32px gap) = ~3792px total width, minus one viewport-width = travel distance
  // We use a conservative percentage that works across screen sizes
  const x = useTransform(scrollYProgress, [0, 1], ['0%', '-72%']);

  return (
    /* Tall outer section — scrolling this drives the horizontal animation */
    <section
      id="projects"
      ref={containerRef}
      className="relative bg-background"
      style={{ height: '500vh' }}
    >
      {/* ---- Sticky viewport wrapper ---- */}
      <div className="sticky top-0 h-screen overflow-hidden flex flex-col">

        {/* Section header — fully outside the scrolling strip */}
        <div className="shrink-0 max-w-7xl w-full mx-auto px-6 pt-28 pb-8 flex items-end justify-between">
          <div>
            <span className="mono text-xs uppercase tracking-widest text-primary mb-3 block">
              // 04 — Selected Work
            </span>
            <h2 className="serif text-4xl sm:text-5xl text-white leading-tight">
              Real engagements,<br />
              <span className="italic text-slate-500">measurable outcomes</span>.
            </h2>
          </div>
          <p className="hidden lg:block text-slate-500 text-sm max-w-xs text-right leading-relaxed">
            Scroll down to browse →
          </p>
        </div>

        {/* ---- Horizontal scrolling strip ---- */}
        <div className="flex-1 flex items-center overflow-visible">
          <motion.div
            style={{ x }}
            className="flex gap-6 pl-6 md:pl-20 will-change-transform"
          >
            {projects.slice(0, 6).map((project, index) => (
              <div
                key={project.id}
                className="relative shrink-0 w-[82vw] sm:w-[520px] md:w-[580px] h-[58vh] sm:h-[62vh] rounded-3xl overflow-hidden group bg-card border border-white/5 hover:border-primary/30 transition-colors duration-500"
              >
                {/* Card background: grid + radial glow */}
                <div className="absolute inset-0 bg-grid-dark opacity-30 pointer-events-none" />
                <div
                  className="absolute inset-0 pointer-events-none transition-transform duration-1000 group-hover:scale-110"
                  style={{
                    background: `radial-gradient(ellipse at ${
                      index % 3 === 0 ? 'top left'
                      : index % 3 === 1 ? 'top right'
                      : 'bottom center'
                    }, rgba(217,119,87,0.18) 0%, transparent 65%)`,
                  }}
                />

                {/* Bottom fade */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent pointer-events-none" />

                {/* Card content */}
                <div className="absolute inset-0 z-10 flex flex-col justify-end p-7 md:p-10">
                  {/* Top row: category + number */}
                  <div className="flex items-center gap-3 mb-4">
                    <span className="mono text-[10px] uppercase tracking-widest text-primary px-2.5 py-1 rounded-full border border-primary/25 bg-primary/10">
                      {project.category}
                    </span>
                    <span className="mono text-[10px] uppercase tracking-widest text-slate-600">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="serif text-2xl sm:text-3xl md:text-4xl text-white leading-tight mb-3 group-hover:text-primary transition-colors duration-400">
                    {project.title}
                  </h3>

                  {/* Summary — reveals on hover */}
                  <p className="text-slate-400 font-light text-sm max-w-sm mb-5 opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                    {project.summary}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {project.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="text-[10px] mono uppercase tracking-wider text-slate-600">
                        #{tag}
                      </span>
                    ))}
                  </div>

                  {/* Impact badge + arrow */}
                  <div className="flex items-center justify-between">
                    <span className="mono text-[10px] uppercase tracking-widest text-slate-500 border border-white/8 rounded-full px-3 py-1">
                      {project.impact}
                    </span>
                    <div className="w-10 h-10 rounded-full bg-white/8 border border-white/10 flex items-center justify-center text-slate-400 group-hover:bg-primary group-hover:border-primary group-hover:text-white group-hover:scale-110 transition-all duration-400">
                      <ArrowUpRight size={17} />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* "View All" end card */}
            <div className="shrink-0 w-64 flex flex-col items-center justify-center gap-5 text-slate-500 pr-10">
              <div className="w-16 h-16 rounded-full border border-white/10 flex items-center justify-center group hover:border-primary hover:text-primary transition-colors cursor-pointer">
                <ArrowUpRight size={26} className="text-slate-500 group-hover:text-primary" />
              </div>
              <span className="serif italic text-xl text-slate-400">All projects</span>
              <span className="mono text-[10px] uppercase tracking-widest text-slate-600">{projects.length} total</span>
            </div>
          </motion.div>
        </div>

        {/* Scroll progress indicator */}
        <div className="shrink-0 px-6 md:px-20 pb-6 flex items-center gap-3">
          <div className="h-[1px] flex-1 bg-white/5 rounded-full overflow-hidden">
            <motion.div
              style={{ scaleX: scrollYProgress, transformOrigin: 'left' }}
              className="h-full bg-primary/60 rounded-full"
            />
          </div>
          <span className="mono text-[10px] uppercase tracking-widest text-slate-600 shrink-0">
            {projects.slice(0, 6).length} projects
          </span>
        </div>
      </div>
    </section>
  );
};

export default Projects;
